import React from 'react';
import { Loader2 } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { ClotureReglages, CarteReglage } from './ClotureReglages';

/**
 * Onglet « Interventions » (2026-10-19, demande d'Emeni : « il faut bien ranger ça »). Tout ce qui décrit COMMENT une prestation se
 * déroule sur place, séparé des horaires d'ouverture et de la réservation en ligne (onglet « Horaires »). Chaque choix s'enregistre
 * tout de suite : pas de bouton « Enregistrer » à chercher.
 *   1. Lieu d'intervention
 *   2. Fin de prestation (qui termine, quelles photos) et envois après la prestation
 *   3. Ce que voit le salarié (prix, encaissement)
 */
export function InterventionsTab() {
    const { refreshUser } = useAuth();
    const [loading, setLoading] = React.useState(true);
    const [lieu, setLieu] = React.useState<'domicile' | 'entreprise' | 'les_deux'>('domicile');
    const [salarieVoitPrix, setSalarieVoitPrix] = React.useState(true);
    const [message, setMessage] = React.useState('');
    const [erreur, setErreur] = React.useState('');

    React.useEffect(() => {
        moduleApi.getPrestationPlanningSettings().then((s: any) => {
            if (s?.location_mode) setLieu(s.location_mode);
            setSalarieVoitPrix(s?.employee_show_prices !== false);
        }).catch(() => setErreur('Chargement impossible.')).finally(() => setLoading(false));
    }, []);

    // Un seul champ envoyé à chaque fois : le serveur garde tout le reste tel quel.
    const enregistrer = async (patch: { location_mode?: string; employee_show_prices?: boolean }) => {
        setMessage(''); setErreur('');
        try {
            const res: any = await moduleApi.savePrestationPlanningSettings(patch);
            if (res?.error) throw new Error(res.error);
            if (patch.location_mode) await refreshUser();
            setMessage('Enregistré.');
        } catch (e: any) { setErreur(e?.message || "L'enregistrement a échoué."); }
    };

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;

    return (
        <div className="max-w-lg space-y-5">
            <CarteReglage titre="Lieu d'intervention" aide="Faut-il préciser le lieu à chaque rendez-vous, ou est-ce toujours le même ?">
                <div className="grid grid-cols-3 gap-2">
                    {([
                        { key: 'domicile', label: 'Chez le client' },
                        { key: 'entreprise', label: "Dans l'entreprise" },
                        { key: 'les_deux', label: 'Les deux' },
                    ] as const).map((opt) => (
                        <button key={opt.key} type="button" onClick={() => { setLieu(opt.key); enregistrer({ location_mode: opt.key }); }}
                            className={`px-3 py-2.5 rounded-xl text-xs font-bold border transition-all ${lieu === opt.key ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                            {opt.label}
                        </button>
                    ))}
                </div>
            </CarteReglage>

            <ClotureReglages />

            <CarteReglage titre="Ce que voit le salarié" aide="Les prix et l'encaissement lors de la fin de prestation.">
                <label className="flex items-start gap-2.5 cursor-pointer">
                    <input type="checkbox" className="accent-brand-600 mt-0.5" checked={salarieVoitPrix}
                        onChange={(e) => { setSalarieVoitPrix(e.target.checked); enregistrer({ employee_show_prices: e.target.checked }); }} />
                    <span className="text-sm text-content"><b>Le salarié voit les prix et peut encaisser</b>
                        <span className="block text-[11px] text-content-muted">Décoché, il ne voit aucun montant : il ne peut ni ajouter de ligne, ni encaisser, ni envoyer la facture. Il fait seulement le constat (photos, signature du client).</span></span>
                </label>
            </CarteReglage>

            {message && <p className="text-xs font-semibold text-green-600 dark:text-green-400">{message}</p>}
            {erreur && <p className="text-xs font-semibold text-red-500">{erreur}</p>}
        </div>
    );
}
